import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Maximize2, MessageSquare } from 'lucide-react';
import { Game } from '../types';
import Header from './Header';
import { games } from '../data/games';
import Footer from './Footer';
import GameSection from './GameSection';
import { useTranslation } from 'react-i18next';
import { useRecentGames } from '../hooks/useRecentGames';
import AdBanner from './AdBanner';

interface GameDetailProps {
  game: Game;
}

export default function GameDetail({ game }: GameDetailProps) {
  const { t } = useTranslation();
  const { addRecentGame } = useRecentGames();
  
  useEffect(() => {
    // 當進入遊戲頁面時，添加到最近玩過列表
    addRecentGame(game);
  }, [game, addRecentGame]);

  // 獲取前4個遊戲作為推薦遊戲（排除當前遊戲）
  const recommendedGames = games
    .filter(g => g.id !== game.id)
    .slice(0, 4);

  const handleFullscreen = () => {
    const gameFrame = document.getElementById('game-frame');
    if (gameFrame) {
      if (gameFrame.requestFullscreen) {
        gameFrame.requestFullscreen();
      }
    }
  };

  const handleFeedback = () => {
    window.location.href = 'mailto:oxo@elkroom.com?subject=Game Feedback: ' + game.title;
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header onSearch={() => {}} />
      
      <div className="flex-1 bg-[#1a1625] pt-16">
        <div className="sticky top-16 bg-[#13111d] border-b border-gray-800 z-40">
          <div className="container mx-auto px-4">
            <div className="flex items-center h-12">
              <Link to="/" className="flex items-center space-x-2 text-white hover:text-purple-400">
                <ArrowLeft className="w-5 h-5" />
                <span>{game.title}</span>
              </Link>
            </div>
          </div>
        </div>
        
        <main className="container mx-auto px-4 py-8">
          {/* Game Embed Container */}
          <div className="relative bg-black rounded-lg overflow-hidden mb-8">
            <iframe
              id="game-frame"
              src={game.url}
              title={game.title}
              className="w-full aspect-video border-0 mb-12"
              allowFullScreen
            />

            {/* Control Bar - 半透明背景條 */}
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-[#13111d]/80 backdrop-blur-sm">
              <div className="container h-full flex items-center justify-end px-4">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleFeedback}
                    className="flex items-center justify-center w-8 h-8 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                    title="Feedback"
                  >
                    <MessageSquare className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleFullscreen}
                    className="flex items-center justify-center w-8 h-8 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                    title="Fullscreen"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Game Info */}
          <div className="bg-[#13111d] rounded-lg p-6 text-white mb-8">
            <h1 className="text-2xl font-bold mb-4">{game.title}</h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-300 mb-4">{game.description}</p>
                
                <div className="space-y-2">
                  {game.developer && (
                    <div className="flex items-center">
                      <span className="text-gray-400 w-32">{t('game.developer')}:</span>
                      <span className="text-white">{game.developer}</span>
                    </div>
                  )}
                  {game.rating && (
                    <div className="flex items-center">
                      <span className="text-gray-400 w-32">{t('game.rating')}:</span>
                      <span className="text-white">
                        {game.rating} ({game.votes} {t('game.votes')})
                      </span>
                    </div>
                  )}
                  {game.releaseDate && (
                    <div className="flex items-center">
                      <span className="text-gray-400 w-32">{t('game.released')}:</span>
                      <span className="text-white">{game.releaseDate}</span>
                    </div>
                  )}
                  {game.lastUpdated && (
                    <div className="flex items-center">
                      <span className="text-gray-400 w-32">{t('game.last_updated')}:</span>
                      <span className="text-white">{game.lastUpdated}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 贊助內容區塊 - 簡化後的區塊 */}
          <div className="bg-[#13111d] rounded-lg p-6 text-white mb-8">
            <h2 className="text-xl font-bold mb-4">{t('game.sponsored_content')}</h2>
            <div className="bg-[#1a1625] p-4 rounded-lg">
              <AdBanner 
                adClient="ca-pub-3576991009345970"
                adSlot="3814341672"
                className="w-full"
                height={50}
                maxHeight={250}
              />
            </div>
          </div>

          {/* More Games Section */}
          <GameSection title="More Games" games={recommendedGames} />
        </main>
      </div>

      <Footer />
    </div>
  );
}